import { useEffect, useRef } from 'react';

const breakdown = [
  {
    label: '실리콘 시공 인건비 (1일)',
    value: '24만원',
    note: '업계 평균 기준',
    icon: 'ri-user-fill',
  },
  {
    label: '1일 작업량',
    value: '약 800개',
    note: '현장 기준',
    icon: 'ri-stack-fill',
  },
  {
    label: '개당 인건비',
    value: '300원',
    note: '24만원 ÷ 800개',
    icon: 'ri-divide-fill',
    highlight: true,
  },
  {
    label: '개당 실리콘 재료비',
    value: '50원',
    note: '실리콘 소모량 기준',
    icon: 'ri-test-tube-fill',
    highlight: true,
  },
];

const savingsCards = [
  {
    amount: '300원',
    label: '개당 인건비 절감',
    icon: 'ri-user-smile-fill',
    color: 'from-orange-400 to-[#FF6B35]',
  },
  {
    amount: '50원',
    label: '개당 재료비 절감',
    icon: 'ri-drop-fill',
    color: 'from-yellow-400 to-orange-400',
  },
  {
    amount: '350원',
    label: '개당 총 절감액',
    icon: 'ri-coin-fill',
    color: 'from-green-400 to-emerald-500',
    big: true,
  },
];

const scenarios = [
  {
    tier: '소규모 현장',
    icon: 'ri-building-2-line',
    daily: '200개',
    dailySave: '7만원',
    monthlySave: '175만원',
    yearlySave: '2,100만원',
    highlight: false,
  },
  {
    tier: '중규모 현장',
    icon: 'ri-building-3-line',
    daily: '800개',
    dailySave: '28만원',
    monthlySave: '700만원',
    yearlySave: '8,400만원',
    highlight: true,
    badge: '기준 사례',
  },
  {
    tier: '대규모 공장 납품',
    icon: 'ri-community-line',
    daily: '2,000개',
    dailySave: '70만원',
    monthlySave: '1,750만원',
    yearlySave: '2억 1,000만원',
    highlight: false,
  },
];

const CostSavingsSection = () => {
  const titleRef = useRef<HTMLDivElement>(null);
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const bannerRef = useRef<HTMLDivElement>(null);
  const simRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
          }
        });
      },
      { threshold: 0.1 }
    );
    [titleRef, leftRef, rightRef, bannerRef, simRef].forEach(
      (r) => r.current && observer.observe(r.current)
    );
    return () => observer.disconnect();
  }, []);

  return (
    <section id="cost-savings" className="py-24 bg-[#1B2A4A]">
      <div className="max-w-5xl mx-auto px-6 lg:px-8">

        {/* 타이틀 */}
        <div
          ref={titleRef}
          className="text-center mb-14 opacity-0 translate-y-6 transition-all duration-700"
        >
          <span className="text-[#FF6B35] text-sm font-semibold tracking-widest uppercase">
            Cost Savings
          </span>
          <h2 className="mt-3 text-3xl lg:text-4xl font-bold text-white font-display">
            Smart Cap 도입 시{' '}
            <span className="text-[#FF6B35]">개당 350원 절감</span>
          </h2>
          <p className="mt-4 text-gray-400 text-base max-w-xl mx-auto">
            실리콘 공정 삭제로 인건비 + 재료비를 동시에 절약합니다
          </p>
          <div className="mt-4 w-12 h-1 bg-[#FF6B35] mx-auto rounded-full" />
        </div>

        {/* 좌우 레이아웃 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">

          {/* 왼쪽: 비용 산출 근거 */}
          <div
            ref={leftRef}
            className="opacity-0 translate-y-8 transition-all duration-700 delay-100"
          >
            <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
              {/* 헤더 */}
              <div className="px-5 py-4 border-b border-white/10 flex items-center gap-2">
                <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#FF6B35]/20">
                  <i className="ri-calculator-fill text-[#FF6B35] text-sm" />
                </div>
                <span className="text-white font-bold text-sm">비용 산출 근거</span>
              </div>

              {/* 항목 리스트 */}
              {breakdown.map((item, i) => (
                <div
                  key={i}
                  className={`flex items-center justify-between px-5 py-4 border-b border-white/5 last:border-0 ${
                    item.highlight ? 'bg-[#FF6B35]/8' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${
                      item.highlight ? 'bg-[#FF6B35]/20' : 'bg-white/8'
                    }`}>
                      <i className={`${item.icon} text-sm ${item.highlight ? 'text-[#FF6B35]' : 'text-gray-400'}`} />
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${item.highlight ? 'text-white' : 'text-gray-300'}`}>
                        {item.label}
                      </p>
                      <p className="text-gray-500 text-xs mt-0.5">{item.note}</p>
                    </div>
                  </div>
                  <span className={`font-bold text-base whitespace-nowrap ${
                    item.highlight ? 'text-[#FF6B35]' : 'text-white'
                  }`}>
                    {item.value}
                  </span>
                </div>
              ))}

              {/* 총합 행 */}
              <div className="px-5 py-4 bg-[#FF6B35]/15 border-t-2 border-[#FF6B35]/40 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#FF6B35]/30">
                    <i className="ri-equal-fill text-[#FF6B35] text-sm" />
                  </div>
                  <p className="text-white font-bold text-sm">개당 총 절감액</p>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-gray-400 text-xs">300원 + 50원</span>
                  <span className="text-[#FF6B35] font-black text-xl">= 350원</span>
                </div>
              </div>
            </div>
          </div>

          {/* 오른쪽: 절감 카드 3개 */}
          <div
            ref={rightRef}
            className="opacity-0 translate-y-8 transition-all duration-700 delay-200 flex flex-col gap-4"
          >
            {savingsCards.map((card, i) => (
              <div
                key={i}
                className={`relative rounded-2xl overflow-hidden ${card.big ? 'ring-2 ring-green-400/40' : ''}`}
              >
                <div className={`bg-gradient-to-r ${card.color} p-0.5 rounded-2xl`}>
                  <div className="bg-[#1B2A4A] rounded-[14px] px-6 py-5 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 flex items-center justify-center rounded-xl bg-gradient-to-br ${card.color} flex-shrink-0`}>
                        <i className={`${card.icon} text-white text-xl`} />
                      </div>
                      <div>
                        <p className="text-gray-400 text-xs font-medium mb-0.5">{card.label}</p>
                        <p className={`font-black text-3xl leading-tight ${card.big ? 'text-green-400' : 'text-white'}`}>
                          {card.amount}
                        </p>
                      </div>
                    </div>
                    {card.big && (
                      <div className="bg-green-400/20 border border-green-400/30 text-green-400 text-xs font-bold px-3 py-1.5 rounded-full whitespace-nowrap">
                        인건비 + 재료비
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 하단 대형 강조 배너 */}
        <div
          ref={bannerRef}
          className="mt-10 opacity-0 translate-y-6 transition-all duration-700 delay-300"
        >
          <div className="bg-gradient-to-r from-[#FF6B35]/20 via-white/5 to-green-500/20 border border-white/10 rounded-2xl px-6 py-7">
            <div className="flex items-center justify-center gap-2 mb-5">
              <i className="ri-bar-chart-box-fill text-[#FF6B35] text-lg" />
              <span className="text-gray-400 text-sm font-semibold tracking-wide">
                하루 800개 시공 기준 누적 절감 효과
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
              <div className="py-4 rounded-xl bg-white/5 border border-white/8">
                <p className="text-gray-400 text-xs mb-1.5 font-medium">일일 절감</p>
                <p className="text-white font-black text-3xl leading-none">28만원</p>
                <p className="text-gray-500 text-xs mt-1.5">350원 × 800개</p>
              </div>
              <div className="py-4 rounded-xl bg-[#FF6B35]/15 border border-[#FF6B35]/30">
                <p className="text-[#FF6B35]/80 text-xs mb-1.5 font-medium">월간 절감</p>
                <p className="text-[#FF6B35] font-black text-3xl leading-none">700만원</p>
                <p className="text-gray-500 text-xs mt-1.5">일 28만원 × 25일</p>
              </div>
              <div className="py-4 rounded-xl bg-green-500/15 border border-green-500/30">
                <p className="text-green-400/80 text-xs mb-1.5 font-medium">연간 절감</p>
                <p className="text-green-400 font-black text-3xl leading-none">8,400만원</p>
                <p className="text-gray-500 text-xs mt-1.5">월 700만원 × 12개월</p>
              </div>
            </div>
          </div>
        </div>

        {/* 절감 효과 시뮬레이션 */}
        <div
          ref={simRef}
          className="mt-10 opacity-0 translate-y-6 transition-all duration-700 delay-400"
        >
          {/* 시뮬레이션 타이틀 */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#FF6B35]/20 flex-shrink-0">
              <i className="ri-function-fill text-[#FF6B35] text-sm" />
            </div>
            <h3 className="text-white font-bold text-lg">절감 효과 시뮬레이션</h3>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {scenarios.map((s, i) => (
              <div
                key={i}
                className={`rounded-2xl overflow-hidden ${
                  s.highlight
                    ? 'ring-2 ring-[#FF6B35] shadow-lg shadow-[#FF6B35]/20'
                    : 'border border-white/10'
                }`}
              >
                {/* 카드 헤더 */}
                <div className={`px-5 py-4 flex items-center gap-2.5 ${
                  s.highlight ? 'bg-[#FF6B35]/20' : 'bg-white/5'
                }`}>
                  <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 ${
                    s.highlight ? 'bg-[#FF6B35]/30' : 'bg-white/10'
                  }`}>
                    <i className={`${s.icon} text-base ${s.highlight ? 'text-[#FF6B35]' : 'text-gray-400'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`font-bold text-sm truncate ${s.highlight ? 'text-white' : 'text-gray-300'}`}>
                      {s.tier}
                    </p>
                    <p className="text-gray-500 text-xs mt-0.5">일 작업량 {s.daily}</p>
                  </div>
                  {s.badge && (
                    <span className="bg-[#FF6B35] text-white text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0">
                      {s.badge}
                    </span>
                  )}
                </div>

                {/* 수치 목록 */}
                <div className="bg-[#0F1E35] px-5 py-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500 text-xs">일 절감액</span>
                    <span className="text-white font-semibold text-sm">{s.dailySave}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500 text-xs">월 절감액 (25일)</span>
                    <span className={`font-semibold text-sm ${s.highlight ? 'text-[#FF6B35]' : 'text-white'}`}>
                      {s.monthlySave}
                    </span>
                  </div>
                  <div className={`flex items-center justify-between pt-2.5 border-t ${
                    s.highlight ? 'border-[#FF6B35]/30' : 'border-white/10'
                  }`}>
                    <span className={`text-xs font-semibold ${s.highlight ? 'text-[#FF6B35]/70' : 'text-gray-400'}`}>
                      연 절감액
                    </span>
                    <span className={`font-black text-xl leading-none ${
                      s.highlight ? 'text-[#FF6B35]' : 'text-green-400'
                    }`}>
                      {s.yearlySave}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <p className="text-gray-600 text-xs mt-4 text-center">
            ※ 개당 절감액 350원(인건비 300원 + 실리콘 재료비 50원) 기준
          </p>
        </div>

      </div>
    </section>
  );
};

export default CostSavingsSection;
